import React, { memo, useState, useRef, useEffect } from 'react';
import { Hr } from 'components/atoms/Hr';
import {
	PayPalButtons,
	PayPalHostedFieldsProvider,
	PayPalHostedField,
	PAYPAL_HOSTED_FIELDS_TYPES,
	FUNDING,
} from '@paypal/react-paypal-js';
import {
	callCreateOrderApi,
	callGenerateBillingAgreementTokenApi,
	callGenerateBillingAgreementIdApi,
} from 'requests/RequestPayment';
import { Payment, isPayPal, isCredit, OrderTypeEntity, Page } from 'consts/PaymentConst';
import { HttpStatus } from 'consts/HttpStatus';
import { CARD_MAX_MESSAGE } from 'consts/CardConst';
import { CircularProgress, Backdrop } from '@material-ui/core';
import { useReceivePaymentRequest } from 'hooks/payment/UseReceivePaymentRequest';
import { useCreditCardForm } from 'hooks/payment/UseCreditCardForm';
import { usePaymentOverview } from 'hooks/payment/UsePaymentOverView';
import { useFraudNet } from 'hooks/payment/UseFraudNet';
import { usePaymentPage } from 'hooks/payment/UsePaymentPage';
import { usePaymentCards } from 'hooks/payment/UsePaymentCards';
import { useAgreeSendingCardInfo } from 'hooks/UseAgreeSendingCardInfo';
import { ErrorTemplate } from './ErrorTemplate';
import { UnSupportedTemplate } from './UnSupportedTemplate';
import { AgreeSendingCardInfo } from 'components/molecules/agree-sending-card-info/AgreeSendingCardInfo';
import classes from './override.module.scss';

type Props = {};

export const PeriodicPaymentTemplate = memo((_props: Props) => {
	const scrollRef = useRef(null);
	const scrollTop = () => {
		scrollRef.current.scrollIntoView();
	};
	const { page, setPage, isDefault, isShowSavedCard, isDeleteCard, isUnSupported, isServiceError } = usePaymentPage();
	const { getOrderLoading, order, redirect_uri, state, error, setError, resetOptions, transErrorPage, siteSettlement } =
		useReceivePaymentRequest(OrderTypeEntity.PERIODIC_PAYMENT, setPage);
	const { f } = useFraudNet(order?.merchant_id);
	const { isAgreeSendCardInfo, handleChangeAgreeCheckValue } = useAgreeSendingCardInfo();
	const {
		isSaveCard,
		payMethod,
		paymentTokenExists,
		handleChangeRadioValue,
		displayCardBrandImage,
		submitLoading,
		setSubmitLoading,
		SubmitPayment,
		redirect,
		monthSelectList,
		isPaymentTokenMax,
	} = useCreditCardForm(
		order,
		transErrorPage,
		redirect_uri,
		state,
		OrderTypeEntity.PERIODIC_PAYMENT,
		resetOptions,
		setError,
		scrollTop,
		isAgreeSendCardInfo,
		true,
	);
	const { PaymentOverview } = usePaymentOverview(order);
	const {
		NEW_CARD_SELECT_VALUE,
		handleChangeSavedCardSelectValue,
		selectedCard,
		isCardSelected,
		selectDeleteCard,
		deleteCardLoading,
		deleteCard,
		deleteCardIndex,
	} = usePaymentCards(Page, setPage, setError, order);

	// PayPalボタン用 定期購読トークン作成(※1ユーザー1回のみ)
	const createBillingAgreementToken = async () => {
		const createBillingAgreementTokenResult = await callGenerateBillingAgreementTokenApi();
		if (createBillingAgreementTokenResult.status !== HttpStatus.OK) {
			transErrorPage();
			setSubmitLoading(false);
			return;
		}
		return createBillingAgreementTokenResult.data.token_id;
	};

	// PayPalボタン用 定期購読ID作成(※1ユーザー1回のみ)
	const onApprove = async (data) => {
		const createBillingAgreementIdResult = await callGenerateBillingAgreementIdApi({
			token_id: data.billingToken,
			// gid_order_no: order.gid_order_no,
		});
		if (createBillingAgreementIdResult.status !== HttpStatus.OK) {
			transErrorPage();
			return;
		}

		const createOrderResult = await callCreateOrderApi({
			gid_order_no: order.gid_order_no,
			agreement_id: createBillingAgreementIdResult.data.agreement_id,
			payment_type: Payment.PAYPAL.key,
		});
		if (createOrderResult.status !== HttpStatus.OK) {
			transErrorPage();
			return;
		}

		redirect(Payment.PAYPAL);
	};

	// クレジットカード用 決済情報作成
	const createOrder = async () => {
		const createOrderResult = await callCreateOrderApi({
			gid_order_no: order.gid_order_no,
			payment_type: Payment.CREDIT.key,
		});
		if (createOrderResult.status !== HttpStatus.OK) {
			transErrorPage();
			return;
		}
		return createOrderResult.data.paypal_order_id;
	};

	const [processLoading, setProcessLoading] = useState(false);
	// 保存済みPayPalアカウントで決済する
	const paySavedPayPal = async () => {
		setProcessLoading(true);
		const createOrderResult = await callCreateOrderApi({
			gid_order_no: order.gid_order_no,
			agreement_id: order.agreement_id,
			payment_type: Payment.PAYPAL.key,
		});
		if (createOrderResult.status !== HttpStatus.OK) {
			transErrorPage();
			setProcessLoading(false);
			return;
		}
		setProcessLoading(false);
		redirect(Payment.PAYPAL);
	};

	// 保存済みクレジットカードで決済する
	const paySavedCreditCard = async () => {
		setProcessLoading(true);
		const createOrderResult = await callCreateOrderApi({
			gid_order_no: order.gid_order_no,
			paypal_client_metadata_id: f,
			payment_tokens_id: order.payment_tokens[selectedCard].payment_id,
			payment_type: Payment.CREDIT.key,
		});
		if (createOrderResult.status !== HttpStatus.OK) {
			transErrorPage();
			setProcessLoading(false);
			return;
		}
		setProcessLoading(false);
		redirect(Payment.CREDIT);
	};

	useEffect(() => {
		if (isServiceError() || isUnSupported()) {
			return;
		}
		setError('');
		scrollTop();
		document.title = 'お支払い方法の選択';
	}, [page]);

	return (
		<main>
			<Backdrop
				className={classes.backdrop}
				open={getOrderLoading || submitLoading || processLoading || deleteCardLoading}
			>
				<CircularProgress color="inherit" />
			</Backdrop>
			{isServiceError() && (
				<ErrorTemplate
					button_display={siteSettlement.error_button_display}
					return_url={siteSettlement.error_return_url}
				/>
			)}

			{isUnSupported() && (
				<UnSupportedTemplate
					button_display={siteSettlement.error_button_display}
					return_url={siteSettlement.error_return_url}
				/>
			)}

			{!isServiceError() && !isUnSupported() && (
				<section className="section mypage" ref={scrollRef}>
					{getOrderLoading ? (
						<div>注文情報取得中</div>
					) : (
						<div className="section-in" style={{ marginTop: 50 }}>
							{isDefault() && (
								<>
									<PaymentOverview error={error} />
									<div className="info-box form-box">
										<div className="form-input">
											{/* PayPal非表示フラグがOFFの場合表示 */}
											{order.payment_specify_list?.paypal_hidden_flg !== 1 && (
												<>
													<div className={classes.sectionPaymentInfoRow}>
														<div className="radio-box">
															<ul>
																<li>
																	<label
																		className={`radio-label ${classes.paymentMethodLabel} ${
																			isPayPal(payMethod) ? 'is-active' : ''
																		}`}
																		htmlFor="paypal"
																	>
																		<input
																			type="radio"
																			id="paypal"
																			name="method"
																			value={Payment.PAYPAL.key}
																			checked={isPayPal(payMethod)}
																			onChange={handleChangeRadioValue}
																		/>
																		PayPal決済
																	</label>
																</li>
															</ul>
														</div>
														<div className={classes.paymentPaypalInfoText}>
															ペイパルのアカウントをお持ちであればIDとパスワードだけで決済が完了します
														</div>
														{/* 定期購読IDがない場合は通常のPayPalボタン */}
														{isPayPal(payMethod) && !order.agreement_id && (
															<PayPalButtons
																// PayPalボタンのみ表示
																fundingSource={FUNDING.PAYPAL}
																style={{ label: 'pay' }}
																// PayPalボタン押下時に発火
																createBillingAgreement={createBillingAgreementToken}
																// PayPalポップアップ閉じた後に発火
																onApprove={(data) => onApprove(data)}
																onError={(error) => {
																	transErrorPage();
																	console.error(`error: ${JSON.stringify(error)}`);
																}}
															/>
														)}
														{/* 定期購読IDがある場合は自作ボタン */}
														{isPayPal(payMethod) && order.agreement_id && (
															<div className="btn-box">
																<div className="btn gr">
																	<button onClick={paySavedPayPal}>次へ</button>
																</div>
															</div>
														)}
													</div>
													<Hr />
												</>
											)}
											{/* クレジットカード非表示フラグがOFFの場合表示 */}
											{order.payment_specify_list?.credit_hidden_flg !== 1 && (
												<>
													<div className={classes.sectionPaymentInfoRow}>
														<div className={classes.paymentCreditHead}>
															<div className="radio-box">
																<ul>
																	<li>
																		<label
																			className={`radio-label ${classes.paymentMethodLabel} ${
																				isCredit(payMethod) ? 'is-active' : ''
																			}`}
																			htmlFor="credit"
																		>
																			<input
																				type="radio"
																				id="credit"
																				name="method"
																				value={Payment.CREDIT.key}
																				checked={isCredit(payMethod)}
																				onChange={handleChangeRadioValue}
																			/>
																			クレジットカード
																		</label>
																	</li>
																</ul>
															</div>
															<div className={`${classes.paymentCreditCardBox} ${classes.flexCenter}`}>
																{isCredit(payMethod) && paymentTokenExists && !isCardSelected(NEW_CARD_SELECT_VALUE)
																	? displayCardBrandImage(order.payment_tokens[selectedCard].brand)
																	: displayCardBrandImage()}
															</div>
														</div>
														{/* 保存済みカード情報IDがないor新規カードが選択されている場合は入力フォーム */}
														{isCredit(payMethod) && (!paymentTokenExists || isCardSelected(NEW_CARD_SELECT_VALUE)) && (
															<PayPalHostedFieldsProvider
																// 次へ押下時にsubmitの前に発火
																createOrder={createOrder}
																// 資格情報が無い場合に発火
																notEligibleError={<div>Your account is not eligible</div>}
															>
																<PayPalHostedField
																	hostedFieldType={PAYPAL_HOSTED_FIELDS_TYPES.NUMBER}
																	options={{ selector: '#card-number', placeholder: '********' }}
																/>
																<PayPalHostedField
																	hostedFieldType={PAYPAL_HOSTED_FIELDS_TYPES.EXPIRATION_MONTH}
																	options={{
																		selector: '#expiration-month',
																		placeholder: 'MM',
																		select: monthSelectList,
																	}}
																/>
																<PayPalHostedField
																	hostedFieldType={PAYPAL_HOSTED_FIELDS_TYPES.EXPIRATION_YEAR}
																	options={{
																		selector: '#expiration-year',
																		placeholder: 'YYYY',
																		select: true,
																	}}
																/>
																<PayPalHostedField
																	hostedFieldType={PAYPAL_HOSTED_FIELDS_TYPES.CVV}
																	options={{ selector: '#cvv', placeholder: '123' }}
																/>
																<div className={classes.paymentCreditCardInput}>
																	<div className="form-box">
																		<div className="form-head">
																			<label htmlFor="">カード番号 （Card Number）</label>
																			<span className="required">必須</span>
																		</div>
																		<div className="form-input width50">
																			<div id="card-number" className={classes.cardField} />
																		</div>
																	</div>
																	<div className="form-box">
																		<div className="form-head">
																			<label htmlFor="">有効期限 （Expiration Date）</label>
																			<span className="required">必須</span>
																		</div>
																		<div className={classes.paymentCreditExpirationDateRow + ` form-input width50`}>
																			<div className={classes.paymentCreditExpirationDateSelectBox}>
																				<div id="expiration-month" className={classes.cardField} />
																			</div>
																			<span className={classes.paymentCreditExpirationDateSlash}>/</span>
																			<div className={classes.paymentCreditExpirationDateSelectBox}>
																				<div id="expiration-year" className={classes.cardField} />
																			</div>
																		</div>
																	</div>
																	<div className="form-box">
																		<div className="form-head">
																			<label htmlFor="">セキュリティコード （Security Codes）</label>
																			<span className="required">必須</span>
																		</div>
																		<div className="form-input width50">
																			<div id="cvv" className={classes.cardField} />
																		</div>
																	</div>
																	<div className="form-box">
																		<div className="form-input">
																			<div className="check-box">
																				<ul>
																					<li>
																						<label
																							className={`check-label ${isSaveCard ? 'is-active' : ''}`}
																							htmlFor="isSaveCard"
																						>
																							<input type="checkbox" id="isSaveCard" checked={isSaveCard} disabled />
																							カード情報を保存する
																						</label>
																					</li>
																				</ul>
																			</div>
																		</div>
																	</div>
																	<AgreeSendingCardInfo
																		isAgreeSendCardInfo={isAgreeSendCardInfo}
																		handleChangeAgreeCheckValue={handleChangeAgreeCheckValue}
																	/>
																</div>
																<div className="btn-box">
																	{paymentTokenExists && (
																		<div className={`btn wh`}>
																			<button onClick={() => setPage(Page.SHOW_SAVED_CARD)}>別のカードで支払う</button>
																		</div>
																	)}
																	<SubmitPayment />
																</div>
															</PayPalHostedFieldsProvider>
														)}
														{/* 保存済みカード情報IDがある&新規カード以外が選択されている場合は閲覧のみ */}
														{isCredit(payMethod) && paymentTokenExists && !isCardSelected(NEW_CARD_SELECT_VALUE) && (
															<>
																<div className={classes.paymentCreditCardInput}>
																	<div className="form-box">
																		<div className="form-head">
																			<label htmlFor="">カード番号 （Card Number）</label>
																		</div>
																		<div className={`${classes.marginLeft20} form-input width50`}>
																			<label htmlFor="">{`**** **** **** ${order.payment_tokens[selectedCard].last_digits}`}</label>
																		</div>
																	</div>
																	<div className="form-box">
																		<div className="form-head">
																			<label htmlFor="">有効期限 （Expiration Date）</label>
																		</div>
																		<div
																			className={`${classes.marginLeft20} ${classes.paymentCreditExpirationDateRow} form-input width50`}
																		>
																			<label htmlFor="">{`${
																				order.payment_tokens[selectedCard].expiry.split('-')[1]
																			} / ${order.payment_tokens[selectedCard].expiry.split('-')[0]}`}</label>
																		</div>
																	</div>
																</div>
																<div className="btn-box">
																	<div className="btn wh">
																		<button onClick={() => setPage(Page.SHOW_SAVED_CARD)}>別のカードで支払う</button>
																	</div>
																	<div className="btn gr">
																		<button onClick={paySavedCreditCard}>このカードで支払う</button>
																	</div>
																</div>
															</>
														)}
														<Hr />
													</div>
												</>
											)}
											<div className="btn-box">
												<div className="btn wh">
													<button onClick={() => redirect(Payment.BACK)}>戻る</button>
												</div>
											</div>
										</div>
									</div>
								</>
							)}
							{isShowSavedCard() && (
								<>
									{/* 別のカードで支払う場合 */}
									<div className={`section-head ${order.logo ? classes.marginTop30 : ''}`}>
										<span>クレジットカード情報</span>
									</div>
									{isPaymentTokenMax && (
										<div className="section-txt-box">
											<div className="txt">
												<div>{CARD_MAX_MESSAGE}</div>
											</div>
										</div>
									)}
									{error !== '' && (
										<div className="error-box">
											<div className="error-box-in">
												<p>{error}</p>
											</div>
										</div>
									)}
									<div className="info-box form-box">
										<div className="form-input">
											<div className="info-box">
												{paymentTokenExists && (
													<>
														<div className={classes.paymentTokenRowHead}>
															<div className={'info-head ' + classes.paymentTokenRowHeadCol} />
															<div className={'info-head ' + classes.paymentTokenRowHeadCol} />
															<div className={'info-head ' + classes.paymentTokenRowHeadCol}>カード番号</div>
															<div className={'info-head ' + classes.paymentTokenRowHeadCol}>有効期限</div>
															<div className={'info-head ' + classes.paymentTokenRowHeadCol} />
														</div>
														{order.payment_tokens.map((t, i) => {
															return (
																<>
																	<Hr />
																	<div className={classes.paymentTokenRow}>
																		<div className="radio-box">
																			<ul>
																				<li className={classes.paymentTokenLi}>
																					<label
																						className={`radio-label ${classes.paymentTokenLabel} ${
																							isCardSelected(i) ? 'is-active' : ''
																						}`}
																						htmlFor={`after${i}`}
																					>
																						<input
																							type="radio"
																							id={`after${i}`}
																							name={`method${i}`}
																							value={i}
																							checked={isCardSelected(i)}
																							onChange={handleChangeSavedCardSelectValue}
																						/>
																					</label>
																					<div className={classes.paymentTokenCol}>
																						{displayCardBrandImage(t.brand)}
																					</div>
																					<div className={classes.paymentTokenCol}>
																						{`**** **** **** ${t.last_digits}`}
																					</div>
																					<div className={classes.paymentTokenCol}>{`${t.expiry.split('-')[1]} / ${
																						t.expiry.split('-')[0]
																					}`}</div>
																					<div
																						className={classes.savedCardDeleteButton}
																						onClick={() => selectDeleteCard(i)}
																					>
																						削除
																					</div>
																				</li>
																			</ul>
																		</div>
																	</div>
																</>
															);
														})}
													</>
												)}
												{/* カード保存上限に達していなかったら新規カード入力選択肢を表示する */}
												{!isPaymentTokenMax && (
													<>
														<Hr />
														<div className={classes.sectionPaymentInfoRow}>
															<div className={classes.paymentCreditHead}>
																<div className="radio-box">
																	<ul>
																		<li>
																			<label
																				className={`radio-label ${classes.paymentMethodLabel} ${
																					isCardSelected(NEW_CARD_SELECT_VALUE) ? 'is-active' : ''
																				}`}
																				htmlFor="credit"
																			>
																				<input
																					type="radio"
																					id="credit"
																					name={`method${NEW_CARD_SELECT_VALUE}`}
																					value={NEW_CARD_SELECT_VALUE}
																					checked={isCardSelected(NEW_CARD_SELECT_VALUE)}
																					onChange={handleChangeSavedCardSelectValue}
																				/>
																				新規クレジットカード
																			</label>
																		</li>
																	</ul>
																</div>
																<div className={`${classes.paymentCreditCardBox} ${classes.flexCenter}`}>
																	{displayCardBrandImage()}
																</div>
															</div>
															<PayPalHostedFieldsProvider
																// 次へ押下時にsubmitの前に発火
																createOrder={createOrder}
																// 資格情報が無い場合に発火
																notEligibleError={<div>Your account is not eligible</div>}
															>
																<PayPalHostedField
																	hostedFieldType={PAYPAL_HOSTED_FIELDS_TYPES.NUMBER}
																	options={{ selector: '#card-number', placeholder: '********' }}
																/>
																<PayPalHostedField
																	hostedFieldType={PAYPAL_HOSTED_FIELDS_TYPES.EXPIRATION_MONTH}
																	options={{
																		selector: '#expiration-month',
																		placeholder: 'MM',
																		select: monthSelectList,
																	}}
																/>
																<PayPalHostedField
																	hostedFieldType={PAYPAL_HOSTED_FIELDS_TYPES.EXPIRATION_YEAR}
																	options={{
																		selector: '#expiration-year',
																		placeholder: 'YYYY',
																		select: true,
																	}}
																/>
																<PayPalHostedField
																	hostedFieldType={PAYPAL_HOSTED_FIELDS_TYPES.CVV}
																	options={{ selector: '#cvv', placeholder: '123' }}
																/>
																<div className={classes.paymentCreditCardInput}>
																	<div className="form-box">
																		<div className="form-head">
																			<label htmlFor="">カード番号 （Card Number）</label>
																			<span className="required">必須</span>
																		</div>
																		<div className="form-input width50">
																			<div id="card-number" className={classes.cardField} />
																		</div>
																	</div>
																	<div className="form-box">
																		<div className="form-head">
																			<label htmlFor="">有効期限 （Expiration Date）</label>
																			<span className="required">必須</span>
																		</div>
																		<div className={classes.paymentCreditExpirationDateRow + ` form-input width50`}>
																			<div className={classes.paymentCreditExpirationDateSelectBox}>
																				<div id="expiration-month" className={classes.cardField} />
																			</div>
																			<span className={classes.paymentCreditExpirationDateSlash}>/</span>
																			<div className={classes.paymentCreditExpirationDateSelectBox}>
																				<div id="expiration-year" className={classes.cardField} />
																			</div>
																		</div>
																	</div>
																	<div className="form-box">
																		<div className="form-head">
																			<label htmlFor="">セキュリティコード （Security Codes）</label>
																			<span className="required">必須</span>
																		</div>
																		<div className="form-input width50">
																			<div id="cvv" className={classes.cardField} />
																		</div>
																	</div>
																	<div className="form-box">
																		<div className="form-input">
																			<div className="check-box">
																				<ul>
																					<li>
																						<label
																							className={`check-label ${isSaveCard ? 'is-active' : ''}`}
																							htmlFor="isSaveCard"
																						>
																							<input type="checkbox" id="isSaveCard" checked={isSaveCard} disabled />
																							カード情報を保存する
																						</label>
																					</li>
																				</ul>
																			</div>
																		</div>
																	</div>
																	<AgreeSendingCardInfo
																		isAgreeSendCardInfo={isAgreeSendCardInfo}
																		handleChangeAgreeCheckValue={handleChangeAgreeCheckValue}
																	/>
																</div>
																{isCardSelected(NEW_CARD_SELECT_VALUE) && (
																	<>
																		<Hr />
																		<div className="btn-box">
																			<div className="btn wh">
																				<button onClick={() => setPage(Page.DEFAULT)}>戻る</button>
																			</div>
																			<SubmitPayment label="このカードで支払う" />
																		</div>
																	</>
																)}
															</PayPalHostedFieldsProvider>
														</div>
													</>
												)}
											</div>
											{!isCardSelected(NEW_CARD_SELECT_VALUE) && (
												<>
													<Hr />
													<div className={classes.sectionPaymentInfoRow}>
														<div className="btn-box">
															<div className="btn wh">
																<button onClick={() => setPage(Page.DEFAULT)}>戻る</button>
															</div>
															<div className="btn gr">
																<button onClick={paySavedCreditCard}>このカードで支払う</button>
															</div>
														</div>
													</div>
												</>
											)}
										</div>
									</div>
								</>
							)}
							{isDeleteCard() && paymentTokenExists && (
								<>
									{/* カード削除の場合 */}
									<div className="section-head" style={order.logo ? { marginTop: 30 } : { marginTop: 0 }}>
										<span>カード情報の削除</span>
									</div>
									<div className="thankyou-box">
										<img
											className={classes.thankyouDeleteImage}
											src="images/delete.svg"
											alt="削除してよろしいでしょうか"
										/>
									</div>
									<p>
										<div>保存済みのクレジットカード情報を削除します</div>
									</p>
									<Hr />
									{error !== '' && (
										<div className="error-box">
											<div className="error-box-in">
												<p>{error}</p>
											</div>
										</div>
									)}
									<div className={classes.paymentCreditCardInput}>
										<div className="form-box">
											<div className="form-head">
												<label htmlFor="">カード番号 （Card Number）</label>
											</div>
											<div className={`${classes.deleteCardNumber} ${classes.marginLeft20} form-input width50`}>
												{displayCardBrandImage(order.payment_tokens[deleteCardIndex]?.brand)}
												<label htmlFor="">{`**** **** **** ${order.payment_tokens[deleteCardIndex]?.last_digits}`}</label>
											</div>
										</div>
										<div className="form-box">
											<div className="form-head">
												<label htmlFor="">有効期限 （Expiration Date）</label>
											</div>
											<div
												className={`${classes.marginLeft20} ${classes.paymentCreditExpirationDateRow} form-input width50`}
											>
												<label htmlFor="">{`${order.payment_tokens[deleteCardIndex]?.expiry.split('-')[1]} / ${
													order.payment_tokens[deleteCardIndex]?.expiry.split('-')[0]
												}`}</label>
											</div>
										</div>
									</div>
									<Hr />
									<div className={classes.sectionPaymentInfoRow}>
										<div className="btn-box">
											<div className="btn wh">
												<button
													onClick={() => {
														setPage(Page.SHOW_SAVED_CARD);
														setError('');
													}}
												>
													戻る
												</button>
											</div>
											<div className="btn gr">
												<button onClick={deleteCard}>削除</button>
											</div>
										</div>
									</div>
								</>
							)}
						</div>
					)}
				</section>
			)}
		</main>
	);
});
